import type { FC } from "react";
import { useState } from "react";
import type {
	CategoryMappingRowDto,
	CategoryMappingStatus,
	CategoryMappingsDto,
	LocationPlatform,
} from "shared";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import {
	useCategoryMappingActions,
	useCategoryMappings,
} from "../settings/useIntegrations";

const STATUS_LABEL: Record<CategoryMappingStatus, string> = {
	auto: "Mapped",
	manual: "Mapped (manual)",
	unmapped: "Needs mapping",
};

const STATUS_COLOR: Record<CategoryMappingStatus, string> = {
	auto: "#198754",
	manual: "#0d6efd",
	unmapped: "#dc3545",
};

function platformLabel(platform: LocationPlatform) {
	if (platform === "facebook") return "Facebook";
	if (platform === "apple") return "Apple";
	if (platform === "bing") return "Bing";
	if (platform === "foursquare") return "Foursquare";
	return platform;
}

const MappingRow: FC<{
	row: CategoryMappingRowDto;
	facebookOptions: Array<{ id: string; name: string }>;
	mappablePlatforms: LocationPlatform[];
	onPickFacebook: (googleCategoryName: string, optionId: string) => void;
}> = ({ row, facebookOptions, mappablePlatforms, onPickFacebook }) => {
	const label =
		row.googleDisplayName ||
		row.googleCategoryName.replace(/^categories\//, "") ||
		"Category";
	const facebookTarget = row.platforms.facebook;
	const selectedId = facebookTarget?.targetCategoryId ?? "";
	// Keep the current selection visible even when filtering the long Graph list.
	const [filter, setFilter] = useState("");
	const filterKey = filter.trim().toLowerCase();
	const filteredOptions = filterKey
		? facebookOptions.filter(
				(opt) =>
					opt.id === selectedId || opt.name.toLowerCase().includes(filterKey),
			)
		: facebookOptions;

	return (
		<div
			style={{
				border: "1px solid #e9ecef",
				borderRadius: 8,
				padding: "10px 12px",
				marginBottom: 8,
			}}
		>
			<div style={{ fontWeight: 600, marginBottom: 8 }}>
				{label}{" "}
				<span style={{ fontWeight: 400, color: "#6c757d", fontSize: 12 }}>
					({row.role})
				</span>
			</div>
			{(["facebook", "apple", "bing", "foursquare"] as LocationPlatform[]).map(
				(platform) => {
					const target = row.platforms[platform];
					if (!target) return null;
					const canMap = mappablePlatforms.includes(platform);
					return (
						<div
							key={platform}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
								flexWrap: "wrap",
								marginBottom: 6,
								fontSize: 13,
							}}
						>
							<span style={{ minWidth: 88 }}>{platformLabel(platform)}</span>
							<span
								style={{
									color: STATUS_COLOR[target.status],
									fontWeight: 600,
								}}
							>
								{STATUS_LABEL[target.status]}
								{target.targetCategoryName
									? ` → ${target.targetCategoryName}`
									: ""}
							</span>
							{canMap && platform === "facebook" && (
								<div
									style={{
										marginLeft: "auto",
										display: "flex",
										gap: 6,
										flexWrap: "wrap",
										alignItems: "center",
									}}
								>
									<input
										aria-label={`Filter Facebook categories for ${label}`}
										placeholder="Search categories…"
										value={filter}
										onChange={(e) => setFilter(e.target.value)}
										style={{ maxWidth: 160 }}
									/>
									<select
										aria-label={`Map ${label} to Facebook category`}
										style={{ maxWidth: 220 }}
										value={selectedId}
										onChange={(e) => {
											onPickFacebook(row.googleCategoryName, e.target.value);
										}}
									>
										<option value="">
											{target.status === "unmapped"
												? "Pick Facebook category…"
												: "Change…"}
										</option>
										{filteredOptions.map((opt) => (
											<option key={opt.id} value={opt.id}>
												{opt.name}
											</option>
										))}
									</select>
								</div>
							)}
							{!canMap && (
								<span style={{ marginLeft: "auto", color: "#adb5bd" }}>
									Coming in Release 2
								</span>
							)}
						</div>
					);
				},
			)}
		</div>
	);
};

export const CategoryMappingPanel: FC<{ locationId: string }> = ({
	locationId,
}) => {
	const mappings = useCategoryMappings(locationId);
	const { updateMappings } = useCategoryMappingActions(locationId);

	if (mappings.isLoading) {
		return (
			<ExtraForm.Group label="Category mapping">
				<div style={{ color: "#6c757d", fontSize: 13 }}>Loading mappings…</div>
			</ExtraForm.Group>
		);
	}

	if (mappings.isError || !mappings.data) {
		return null;
	}

	const data: CategoryMappingsDto = mappings.data;
	if (data.categories.length === 0) {
		return (
			<ExtraForm.Group label="Category mapping">
				<div style={{ color: "#6c757d", fontSize: 13 }}>
					Save a primary category first, then map it to other platforms.
				</div>
			</ExtraForm.Group>
		);
	}

	return (
		<ExtraForm.Group
			label={
				data.unmappedCount > 0
					? `Category mapping (${data.unmappedCount} unmapped)`
					: "Category mapping"
			}
		>
			{data.unmappedCount > 0 && (
				<div
					style={{
						background: "#fff3cd",
						border: "1px solid #ffecb5",
						borderRadius: 6,
						padding: "8px 10px",
						marginBottom: 10,
						fontSize: 13,
					}}
				>
					Some Google categories have no Facebook match yet. Pick one so Publish
					everywhere can stay consistent.
				</div>
			)}
			{data.categories.map((row: CategoryMappingRowDto) => (
				<MappingRow
					key={row.googleCategoryName}
					row={row}
					facebookOptions={data.facebookCategoryOptions}
					mappablePlatforms={data.mappablePlatforms}
					onPickFacebook={(googleCategoryName, optionId) => {
						const opt = data.facebookCategoryOptions.find(
							(o: { id: string; name: string }) => o.id === optionId,
						);
						if (!opt) return;
						updateMappings.mutate({
							overrides: [
								{
									googleCategoryName,
									platform: "facebook",
									targetCategoryId: opt.id,
									targetCategoryName: opt.name,
								},
							],
						});
					}}
				/>
			))}
		</ExtraForm.Group>
	);
};
